import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ChatBubbleLeftRightIcon,
    PhoneIcon,
    SpeakerWaveIcon,
    SpeakerXMarkIcon,
} from "@heroicons/react/24/outline";
import {
    DemoMode,
    IndustryUseCase,
    ScriptTurn,
    SpeakerRole,
    WIDGET_TYPE_BADGE,
    WIDGET_TYPE_LABEL,
} from "../../data/industryUseCases";

/**
 * IndustryDemo — animated voice/chat conversation card for a single
 * industry, with a Web Audio sound layer.
 *
 * Reused on the per-industry landing pages (`/use-cases/:slug`) and on
 * the `/use-cases` gallery preview. Defaults muted because browsers
 * suspend audio contexts created without a user gesture; the toggle in
 * the top-right resumes the context on first click.
 */

/**
 * Tiny Web Audio synth — produces UI sounds without shipping any audio
 * files. The AudioContext is created lazily on first user gesture.
 */
class SfxEngine {
    private ctx: AudioContext | null = null;
    private masterGain: GainNode | null = null;

    private ensure(): AudioContext | null {
        if (typeof window === "undefined") return null;
        if (this.ctx) return this.ctx;
        const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!Ctor) return null;
        try {
            this.ctx = new Ctor();
        } catch {
            return null;
        }
        if (!this.ctx) return null;
        const g = this.ctx.createGain();
        g.gain.value = 0.6;
        g.connect(this.ctx.destination);
        this.masterGain = g;
        return this.ctx;
    }

    resume(): void {
        const ctx = this.ensure();
        if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    }

    chatPing(direction: "send" | "receive"): void {
        const ctx = this.ensure();
        if (!ctx || !this.masterGain) return;
        const t = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        const startFreq = direction === "send" ? 880 : 660;
        const endFreq = direction === "send" ? 1100 : 520;
        osc.frequency.setValueAtTime(startFreq, t);
        osc.frequency.exponentialRampToValueAtTime(endFreq, t + 0.06);
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.18, t + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
        osc.connect(gain).connect(this.masterGain);
        osc.start(t);
        osc.stop(t + 0.2);
    }

    voiceBlip(role: SpeakerRole): void {
        const ctx = this.ensure();
        if (!ctx || !this.masterGain) return;
        const t = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(role === "AI" ? 480 : 320, t);
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.07, t + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.11);
        osc.connect(gain).connect(this.masterGain);
        osc.start(t);
        osc.stop(t + 0.13);
    }

    /** Brief two-tone "pickup" — played when the call industry switches in voice mode. */
    phonePickup(): void {
        const ctx = this.ensure();
        if (!ctx || !this.masterGain) return;
        const t = ctx.currentTime;
        const tones: Array<{ f: number; start: number; dur: number }> = [
            { f: 720, start: 0.0, dur: 0.08 },
            { f: 540, start: 0.09, dur: 0.12 },
        ];
        for (const { f, start, dur } of tones) {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(f, t + start);
            gain.gain.setValueAtTime(0.0001, t + start);
            gain.gain.exponentialRampToValueAtTime(0.13, t + start + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.0001, t + start + dur);
            osc.connect(gain).connect(this.masterGain);
            osc.start(t + start);
            osc.stop(t + start + dur + 0.03);
        }
    }
}

interface DisplayedMessage extends ScriptTurn {
    id: string;
}

interface ConversationCardProps {
    industry: IndustryUseCase;
    mode: DemoMode;
    soundOn: boolean;
    sfxRef: React.MutableRefObject<SfxEngine>;
    onScriptComplete: () => void;
    cycleKey: number;
}

const ConversationCard: React.FC<ConversationCardProps> = ({
    industry,
    mode,
    soundOn,
    sfxRef,
    onScriptComplete,
    cycleKey,
}) => {
    const script = mode === "voice" ? industry.voiceScript : industry.chatScript;
    const [turnIndex, setTurnIndex] = useState(0);
    const [displayed, setDisplayed] = useState<DisplayedMessage[]>([]);
    const [typedText, setTypedText] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [typingRole, setTypingRole] = useState<SpeakerRole | null>(null);
    const [callElapsed, setCallElapsed] = useState(0);
    const transcriptRef = useRef<HTMLDivElement | null>(null);
    const completedRef = useRef(false);

    // Reset transcript when industry, mode, or external cycle key flips.
    useEffect(() => {
        setTurnIndex(0);
        setDisplayed([]);
        setTypedText("");
        setIsTyping(false);
        setTypingRole(null);
        setCallElapsed(0);
        completedRef.current = false;
    }, [industry.key, mode, cycleKey]);

    // Voice mode "live call" timer.
    useEffect(() => {
        if (mode !== "voice") return;
        const id = window.setInterval(() => setCallElapsed((s) => s + 1), 1000);
        return () => window.clearInterval(id);
    }, [mode, industry.key, cycleKey]);

    // Per-industry pickup tone — single play at start of voice mode script.
    useEffect(() => {
        if (mode !== "voice" || !soundOn) return;
        sfxRef.current.phonePickup();
    }, [industry.key, mode, soundOn, cycleKey, sfxRef]);

    // Type-out simulation, mirrors Hero with slightly faster pacing.
    useEffect(() => {
        if (turnIndex >= script.length) {
            if (!completedRef.current) {
                completedRef.current = true;
                const t = window.setTimeout(() => onScriptComplete(), 2400);
                return () => window.clearTimeout(t);
            }
            return;
        }

        const current = script[turnIndex];
        setTypingRole(current.role);
        setTypedText("");

        const preDelay = turnIndex === 0 ? 350 : 600;
        const charDelay = 22;

        let typingInterval: number | undefined;
        let postTimeout: number | undefined;

        const startTimeout = window.setTimeout(() => {
            setIsTyping(true);
            let i = 0;
            typingInterval = window.setInterval(() => {
                i += 1;
                setTypedText(current.text.slice(0, i));
                if (i >= current.text.length) {
                    if (typingInterval !== undefined) window.clearInterval(typingInterval);
                    setDisplayed((prev) => {
                        const id = `${industry.key}-${mode}-${cycleKey}-${turnIndex}`;
                        if (prev.some((m) => m.id === id)) return prev;
                        return [...prev, { ...current, id }];
                    });
                    if (soundOn) {
                        if (mode === "chat") {
                            sfxRef.current.chatPing(current.role === "AI" ? "receive" : "send");
                        } else {
                            sfxRef.current.voiceBlip(current.role);
                        }
                    }
                    setIsTyping(false);
                    setTypingRole(null);
                    setTypedText("");
                    postTimeout = window.setTimeout(() => setTurnIndex((p) => p + 1), 900);
                }
            }, charDelay);
        }, preDelay);

        return () => {
            window.clearTimeout(startTimeout);
            if (typingInterval !== undefined) window.clearInterval(typingInterval);
            if (postTimeout !== undefined) window.clearTimeout(postTimeout);
        };
    }, [turnIndex, script, industry.key, mode, cycleKey, soundOn, sfxRef, onScriptComplete]);

    // Auto-scroll transcript into view.
    useEffect(() => {
        const el = transcriptRef.current;
        if (!el) return;
        const id = window.setTimeout(() => {
            el.scrollTop = el.scrollHeight;
        }, 40);
        return () => window.clearTimeout(id);
    }, [displayed.length, typedText, isTyping]);

    const Icon = industry.Icon;
    const userLabel = mode === "voice" ? "Caller" : "Visitor";
    const headerSubtitle = mode === "voice"
        ? `${industry.language} · +1 (555) 010-2025`
        : `${industry.language} · acme.com chat widget`;

    const formatElapsed = (s: number) => {
        const mm = Math.floor(s / 60).toString().padStart(2, "0");
        const ss = (s % 60).toString().padStart(2, "0");
        return `${mm}:${ss}`;
    };

    return (
        <div className="relative">
            <motion.div
                className={`absolute -inset-2 rounded-3xl blur-2xl ${industry.softAccent}`}
                animate={{ opacity: [0.45, 0.7, 0.45] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="relative rounded-3xl border border-white/10 bg-slate-900/70 backdrop-blur-xl p-5 sm:p-6 shadow-2xl overflow-hidden">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className={`h-10 w-10 sm:h-11 sm:w-11 shrink-0 rounded-xl bg-gradient-to-br ${industry.accent} flex items-center justify-center shadow-lg`}>
                            <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-white font-semibold text-sm sm:text-base truncate">
                                {industry.label}
                            </div>
                            <div className="text-xs text-slate-400 truncate">{headerSubtitle}</div>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                        <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-r ${
                                WIDGET_TYPE_BADGE[industry.callWidgetType]
                            }`}
                        >
                            {WIDGET_TYPE_LABEL[industry.callWidgetType]}
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300">
                            <motion.span
                                className="h-1.5 w-1.5 rounded-full bg-emerald-400"
                                animate={{ opacity: [0.4, 1, 0.4] }}
                                transition={{ duration: 1.4, repeat: Infinity }}
                            />
                            {mode === "voice" ? `Live · ${formatElapsed(callElapsed)}` : "Live chat"}
                        </span>
                    </div>
                </div>

                <div
                    ref={transcriptRef}
                    className="relative flex flex-col gap-2 max-h-[18rem] sm:max-h-[20rem] overflow-y-auto pr-1.5 scroll-smooth"
                >
                    <AnimatePresence initial={false}>
                        {displayed.map((m) => {
                            const isAI = m.role === "AI";
                            const aiBubble = mode === "voice"
                                ? `bg-gradient-to-r ${industry.accent} text-white`
                                : "bg-slate-800/90 text-slate-100";
                            const userBubble = mode === "voice"
                                ? "bg-slate-800/80 text-slate-100"
                                : "bg-gradient-to-r from-orange-500 to-pink-500 text-white";
                            return (
                                <motion.div
                                    key={m.id}
                                    initial={{ opacity: 0, y: 8, scale: 0.97 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.22, ease: "easeOut" }}
                                    className={`w-fit max-w-[85%] rounded-2xl px-3.5 py-2 text-sm shadow ${
                                        isAI ? `${aiBubble} self-start` : `${userBubble} self-end ml-auto`
                                    }`}
                                >
                                    <div className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">
                                        {isAI ? "AI Agent" : userLabel}
                                    </div>
                                    <div className="mt-0.5 leading-snug">{m.text}</div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>

                    {isTyping && typingRole && (
                        <motion.div
                            key="typing-bubble"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`w-fit max-w-[85%] rounded-2xl px-3.5 py-2 text-sm shadow ${
                                typingRole === "AI"
                                    ? mode === "voice"
                                        ? `bg-gradient-to-r ${industry.accent} text-white self-start`
                                        : "bg-slate-800/90 text-slate-100 self-start"
                                    : mode === "voice"
                                        ? "bg-slate-800/80 text-slate-100 self-end ml-auto"
                                        : "bg-gradient-to-r from-orange-500 to-pink-500 text-white self-end ml-auto"
                            }`}
                        >
                            <div className="text-[10px] uppercase tracking-wider opacity-70 font-semibold flex items-center gap-2">
                                <span>{typingRole === "AI" ? "AI Agent" : userLabel}</span>
                                <span className="opacity-80 normal-case tracking-normal text-[10px]">
                                    {mode === "voice" ? "speaking…" : "typing…"}
                                </span>
                            </div>
                            <div className="mt-0.5 leading-snug">
                                {typedText}
                                <span className="inline-block w-1.5 h-3.5 ml-0.5 align-middle bg-white/70 animate-pulse" />
                            </div>
                        </motion.div>
                    )}
                </div>

                <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2 text-[11px] text-slate-400 flex-wrap">
                    <span className="opacity-70">Pipeline:</span>
                    {industry.pipelineSteps.map((step, i, arr) => (
                        <span key={step} className="flex items-center gap-1.5">
                            <span className="rounded-md bg-slate-800/80 border border-white/5 px-2 py-0.5 text-slate-200">
                                {step}
                            </span>
                            {i < arr.length - 1 && <span className="text-slate-600">→</span>}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};

const ModeToggle: React.FC<{ mode: DemoMode; onChange: (m: DemoMode) => void }> = ({ mode, onChange }) => (
    <div className="inline-flex rounded-full border border-white/15 bg-white/5 p-1">
        {([
            { id: "voice" as DemoMode, label: "Voice", Icon: PhoneIcon },
            { id: "chat" as DemoMode, label: "Chat", Icon: ChatBubbleLeftRightIcon },
        ]).map((opt) => (
            <button
                key={opt.id}
                type="button"
                onClick={() => onChange(opt.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                    mode === opt.id
                        ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow"
                        : "text-white/70 hover:text-white"
                }`}
            >
                <opt.Icon className="h-3 w-3" />
                {opt.label}
            </button>
        ))}
    </div>
);

const SoundToggle: React.FC<{ enabled: boolean; onChange: (v: boolean) => void }> = ({ enabled, onChange }) => (
    <button
        type="button"
        onClick={() => onChange(!enabled)}
        aria-label={enabled ? "Mute sound" : "Unmute sound"}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-white/80 hover:text-white hover:bg-white/10 transition"
    >
        {enabled ? <SpeakerWaveIcon className="h-3.5 w-3.5" /> : <SpeakerXMarkIcon className="h-3.5 w-3.5" />}
        <span>{enabled ? "Sound on" : "Play sound"}</span>
    </button>
);

interface IndustryDemoProps {
    industry: IndustryUseCase;
    /** Default mode on mount. */
    initialMode?: DemoMode;
    /** Show controls row (mode toggle + sound). Defaults to true. */
    showControls?: boolean;
}

/**
 * Self-contained voice/chat demo for a single industry. Loops the script
 * indefinitely and exposes mode + sound toggles when `showControls=true`.
 */
const IndustryDemo: React.FC<IndustryDemoProps> = ({
    industry,
    initialMode = "voice",
    showControls = true,
}) => {
    const [mode, setMode] = useState<DemoMode>(initialMode);
    const [soundOn, setSoundOn] = useState(false);
    const [cycleKey, setCycleKey] = useState(0);
    const sfxRef = useRef<SfxEngine>(new SfxEngine());

    const handleSoundToggle = (next: boolean) => {
        setSoundOn(next);
        if (next) sfxRef.current.resume();
    };

    const handleScriptComplete = () => {
        setCycleKey((k) => k + 1);
    };

    const handleModeChange = (m: DemoMode) => {
        setMode(m);
        setCycleKey((k) => k + 1);
    };

    return (
        <div>
            {showControls && (
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <ModeToggle mode={mode} onChange={handleModeChange} />
                    <SoundToggle enabled={soundOn} onChange={handleSoundToggle} />
                </div>
            )}
            <AnimatePresence mode="wait">
                <motion.div
                    key={`${industry.key}-${mode}`}
                    initial={{ opacity: 0, y: 14, scale: 0.985 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.985 }}
                    transition={{ duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                    <ConversationCard
                        industry={industry}
                        mode={mode}
                        soundOn={soundOn}
                        sfxRef={sfxRef}
                        onScriptComplete={handleScriptComplete}
                        cycleKey={cycleKey}
                    />
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default IndustryDemo;
